import { useEffect, useState } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { Plus, PencilSquare } from "react-bootstrap-icons";
import Swal from "sweetalert2";
import api from "../api/axios";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [myRoles, setMyRoles] = useState([]);

  const [search, setSearch] = useState("");
  const [errors, setErrors] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);

  const roleLabels = {
    admin: "Administrador",
    operator: "Operador",
    analyst: "Analista",
    tenant: "Empresa",
  };

  const [formData, setFormData] = useState({
    id: null,
    name: "",
    email: "",
    password: "",
    tenant_id: "",
    role: "",
    is_active: true,
  });

  // ==============================
  // Fetch inicial
  // ==============================
  useEffect(() => {
    api.get('/user')
      .then(res => {
        setMyRoles(res.data.roles || []);
      })
      .catch(() => setMyRoles([]));
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchTenants();
  }, []);

  const normalize = (txt) =>
    txt?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const fetchUsers = async () => {
    try {
      setLoadingTable(true);
      const res = await api.get("/users/list");
      setUsers(res.data.data);
      setFilteredUsers(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingTable(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const res = await api.get("/tenants/accessible");
      setTenants(res.data.data);
    } catch (err) {
      console.error(err);
    }
  };

  // ==============================
  // Filtro de búsqueda
  // ==============================
  useEffect(() => {
    const result = users.filter(
      (u) =>
        normalize(u.name)?.includes(normalize(search)) ||
        normalize(u.email)?.includes(normalize(search))
    );
    setFilteredUsers(result);
  }, [search, users]);

  // ==============================
  // Modal Nuevo
  // ==============================
  const handleShowNew = () => {
    setIsEditing(false);
    setErrors({});
    setFormData({
      id: null,
      name: "",
      email: "",
      password: "",
      tenant_id: "",
      role: "",
      is_active: true,
    });
    setShowModal(true);
  };

  // ==============================
  // Modal Editar
  // ==============================
  const handleShowEdit = (u) => {
    setIsEditing(true);
    setErrors({});
    setFormData({
      id: u.id,
      name: u.name,
      email: u.email,
      password: "",
      tenant_id: u.tenant_id,
      role: u.roles[0]?.name || "",
      is_active: u.is_active,
    });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setIsEditing(false);
  };

  // ==============================
  // Guardar (crear o editar)
  // ==============================
  const handleSave = async () => {
  setLoadingSave(true);
  setErrors({});

  // --- Validación inmediata en frontend ---
  if (!formData.name) {
    setErrors({ name: ["El nombre es obligatorio."] });
    setLoadingSave(false);
    return;
  }

  if (!formData.email) {
    setErrors({ email: ["El correo es obligatorio."] });
    setLoadingSave(false);
    return;
  }

  if (!isEditing && !formData.password) {
    setErrors({ password: ["La contraseña es obligatoria."] });
    setLoadingSave(false);
    return;
  }

  if (!formData.role) {
    setErrors({ role: ["Debe seleccionar un rol."] });
    setLoadingSave(false);
    return;
  }

  if (formData.role !== "admin" && !formData.tenant_id) {
    setErrors({ tenant_id: ["Debe seleccionar una empresa."] });
    setLoadingSave(false);
    return;
  }

  if (!myRoles.includes("admin") && formData.role === "admin") {
    setErrors({ role: ["No tiene permisos para asignar el rol Administrador."] });
    setLoadingSave(false);
    return;
  }


  // --- Payload corregido ---
  const payload = {
    ...formData,
    name: formData.name?.toUpperCase(),
    tenant_id: formData.tenant_id === null ? null : Number(formData.tenant_id)
  };

  if (isEditing && !payload.password) delete payload.password;

  try {
    if (isEditing) {
      await api.put(`/users/update/${formData.id}`, payload);
    } else {
      await api.post("/users/create", payload);
    }

    fetchUsers();
    handleClose();
  } catch (err) {
    if (err.response?.status === 422) {
      setErrors(err.response.data.errors);
    } else {
      Swal.fire({
        title: "Error",
        text: "Error desconocido al guardar",
        icon: "error",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  } finally {
    setLoadingSave(false);
  }
};


  // ==============================
  // Columnas tabla
  // ==============================
  const columns = [
    { name: "Nombre", selector: (row) => row.name, sortable: true },
    { name: "Correo electrónico", selector: (row) => row.email, sortable: true },
    {
      name: "Rol",
      selector: (row) => roleLabels[row.roles[0]?.name] || "-",
      sortable: true,
    },
    {
      name: "Empresa",
      selector: (row) => row.tenant?.name || "-",
      sortable: true,
    },
    {
      name: "Estado",
      cell: (row) => (
        <span className={`badge ${row.is_active ? "bg-success" : "bg-danger"}`}>
          {row.is_active ? "Activo" : "Inactivo"}
        </span>
      ),
    },
    {
      name: "Opciones",
      cell: (row) => (
        <Button
          variant="outline-info"
          size="sm"
          onClick={() => handleShowEdit(row)}
        >
          <PencilSquare />
        </Button>
      ),
      ignoreRowClick: true,
    },
  ];

  return (
    <div className="mt-4">
      {/* TABLA */}
      <DataTable
        title="Usuarios"
        columns={columns}
        data={filteredUsers}
        pagination
        highlightOnHover
        responsive
        striped
        progressPending={loadingTable}
        progressComponent={
          <div className="text-center p-3">
            <div className="spinner-border text-primary"></div>
            <p className="mt-2">Cargando datos...</p>
          </div>
        }
        subHeader
        subHeaderComponent={
          <div className="d-flex justify-content-between w-100">
            <Button variant="outline-success" size="sm" onClick={handleShowNew}>
              Nuevo <Plus />
            </Button>

            <input
              type="text"
              className="form-control w-25"
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        }
      />

      {/* MODAL */}
      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? "Editar Usuario" : "Nuevo Usuario"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {/* Nombre */}
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
              {errors.name?.map((e, i) => (
                <Form.Text key={i} className="text-danger d-block">
                  {e}
                </Form.Text>
              ))}
            </Form.Group>

            {/* Email */}
            <Form.Group className="mb-3">
              <Form.Label>Correo electrónico</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
              {errors.email?.map((e, i) => (
                <Form.Text key={i} className="text-danger d-block">
                  {e}
                </Form.Text>
              ))}
            </Form.Group>

            {/* Password */}
            <Form.Group className="mb-3">
              <Form.Label>
                Contraseña{" "}
                {isEditing && <small className="text-muted">(opcional)</small>}
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
              />
              {errors.password?.map((e, i) => (
                <Form.Text key={i} className="text-danger d-block">
                  {e}
                </Form.Text>
              ))}
            </Form.Group>

            {/* Rol */}
            <Form.Group className="mb-3">
              <Form.Label>Rol</Form.Label>
              <Form.Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <option value="">Seleccionar...</option>
                {Object.entries(roleLabels)
                  .filter(([value]) => {
                    if (value === "admin" && !myRoles.includes("admin")) {
                      return false; // ⚠ no mostrar admin a usuarios que no sean admin
                    }
                    return true;
                  })
                  .map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                ))}
              </Form.Select>

              {errors.role?.map((e, i) => (
                <Form.Text key={i} className="text-danger d-block">
                  {e}
                </Form.Text>
              ))}
            </Form.Group>

            {/* Empresa */}
            <Form.Group className="mb-3">
              <Form.Label>Empresa (opcional)</Form.Label>
              <Form.Select
                value={formData.tenant_id ?? ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tenant_id: e.target.value === "" ? null : Number(e.target.value)
                  })
                }
              >
                <option value="">Sin empresa</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Form.Select>
              {errors.tenant_id?.map((e, i) => (
                <Form.Text key={i} className="text-danger d-block">
                  {e}
                </Form.Text>
              ))}
            </Form.Group>

            {/* Estado */}
            <Form.Check
              type="checkbox"
              label="Activo"
              checked={formData.is_active}
              onChange={(e) =>
                setFormData({ ...formData, is_active: e.target.checked })
              }
            />
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>

          <Button variant="primary" onClick={handleSave} disabled={loadingSave}>
            {loadingSave ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Guardando...
              </>
            ) : isEditing ? (
              "Actualizar"
            ) : (
              "Guardar"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
